import { useCallback, useEffect, useRef, useState } from 'react';
import { useGetFolderAssetsLazyQuery } from '@frameio/assets/shared/gql/generated/GetFolderAssets';
import { getFolderAssetsConnection } from '@frameio/assets/shared/utils/getFolderAssetsConnection';
import { useApolloClient } from '@frameio/graphql-client';
import {
  useActiveAccountId,
  useActiveFolderId,
} from '@frameio/shared/contexts';
import type {
  ProjectTreeItem,
  ProjectTreeItemType,
  WindowedProjectTree,
} from '../types';
import { FolderQueryInfo, PAGE_SIZE } from '../utils';

export function useProjectFolders(
  rootFolderId?: string,
  skipAncestors = false
) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [ancestorsLoading, setAncestorsLoading] = useState(true);
  const [tree, setTree] = useState<WindowedProjectTree>({
    children: [],
    isOpen: false,
    props: {
      id: rootFolderId ?? '',
      type: 'root-folder',
    },
  });

  const accountId = useActiveAccountId();
  const activeFolderId = useActiveFolderId();
  const client = useApolloClient();
  const folderQueries = useRef<Map<string, FolderQueryInfo>>(new Map());
  const [getFolderAssets] = useGetFolderAssetsLazyQuery();

  const updateTree = useCallback(() => {
    if (!rootFolderId) return;

    setTree(toTree(rootFolderId, rootFolderId, folderQueries.current));
  }, [rootFolderId]);

  /**
   * This effect is used to unsubscribe to any ObservableQueries that are being
   * watched on the current page.
   */
  useEffect(() => {
    const currentFolderQueries = folderQueries.current;

    return () => {
      currentFolderQueries.forEach((folderQuery) => {
        folderQuery.subscription?.unsubscribe();
      });
      currentFolderQueries.clear();
    };
  }, [accountId, rootFolderId]);

  /**
   * This effect fetches the first page of folders that are direct children
   * of the root folder.
   */
  useEffect(() => {
    if (!accountId || !rootFolderId) return;

    setError(false);

    if (folderQueries.current.has(rootFolderId)) {
      // update the tree when the root folder id changes
      updateTree();
      setIsLoading(false);
      return;
    }

    const folderQuery = new FolderQueryInfo(rootFolderId, accountId);

    folderQueries.current.set(rootFolderId, folderQuery);

    const fetchTopLevelFolders = async () => {
      try {
        await fetchFolders(getFolderAssets, accountId, rootFolderId);
      } catch (_) {
        setError(true);
      } finally {
        folderQuery.startWatching(client, () => {
          updateTree();
        });

        setIsLoading(false);
      }
    };

    fetchTopLevelFolders();
  }, [
    accountId,
    client,
    getFolderAssets,
    skipAncestors,
    rootFolderId,
    updateTree,
  ]);

  /**
   * This effect attempts to expand the ancestors of the active folder in the
   * event that a user deep links to a nested folder. See the implementation of
   * FolderQueryInfo.fetchAncestors for more information.
   */
  useEffect(() => {
    if (
      activeFolderId === undefined ||
      activeFolderId === rootFolderId ||
      !accountId ||
      skipAncestors
    ) {
      setAncestorsLoading(false);
      return;
    }

    const activeFolderQuery = new FolderQueryInfo(activeFolderId, accountId);

    activeFolderQuery.fetchAncestors(
      client,
      (id) => {
        const existing = folderQueries.current.get(id);

        if (existing) {
          existing.isExpanded = true;
          updateTree();
          return;
        }

        const folderQuery = new FolderQueryInfo(id, accountId);

        folderQuery.startWatching(client, updateTree);
        folderQueries.current.set(id, folderQuery);
      },
      () => {
        setAncestorsLoading(false);
      }
    );
  }, [
    accountId,
    activeFolderId,
    client,
    rootFolderId,
    skipAncestors,
    updateTree,
  ]);

  const toggleFolder = useCallback(
    async (item: ProjectTreeItem, expanded: boolean) => {
      if (!accountId) return;
      if (item.type !== 'folder' && item.type !== 'root-folder') return;

      const folderId = item.id;

      if (!expanded) {
        folderQueries.current.get(folderId)?.stopWatching();
        updateTree();
      } else {
        const hasBeenExpandedBefore = folderQueries.current.has(folderId);
        const folderQuery =
          folderQueries.current.get(folderId) ??
          new FolderQueryInfo(folderId, accountId);

        folderQueries.current.set(folderId, folderQuery);

        folderQuery.isExpanded = true;

        if (!hasBeenExpandedBefore) {
          // only show the loading spinner the first time we load folders
          folderQuery.isLoading = true;
        }

        updateTree();

        try {
          await fetchFolders(getFolderAssets, accountId, folderId);
        } catch (_) {
          setError(true);
        } finally {
          folderQuery.isLoading = false;
          folderQuery.startWatching(client, updateTree);
        }
      }
    },
    [accountId, client, getFolderAssets, updateTree]
  );

  const fetchMoreFolders = useCallback(
    (type: ProjectTreeItemType, parentId?: string) => {
      if (type === 'folder' && parentId) {
        folderQueries.current.get(parentId)?.fetchMore();
      }
    },
    []
  );

  return {
    error,
    fetchMoreFolders,
    isLoading: isLoading || ancestorsLoading,
    toggleFolder,
    tree,
  };
}

export const EMPTY_FOLDER_ID = 'EMPTY_FOLDER';

/**
 * Recursively builds the folder tree as a nested array to be fed directly into
 * an instance of WindowedTreeView.
 *
 * @param id The current folder id
 * @param parentId The id of the current folder's parent folder
 * @param folderQueries A map of folder id to instance of FolderQueryInfo
 * @returns tree The current state of the folder tree
 */
function toTree(
  id: string,
  parentId: string,
  folderQueries: Map<string, FolderQueryInfo>
): WindowedProjectTree {
  const folderQuery = folderQueries.get(id);
  const isBoundary = folderQueries.get(parentId)?.isCursorBoundary(id) ?? false;
  const isLoading = folderQuery?.isLoading ?? false;
  const isOpen = folderQuery?.isExpanded ?? false;

  let children: WindowedProjectTree['children'] = [];
  const childNodes = folderQuery?.folderChildren?.nodes?.filter(
    (asset) => asset !== null
  );

  if (childNodes && childNodes.length > 0) {
    children = childNodes.map((child) =>
      child.id
        ? toTree(child.id, id, folderQueries)
        : ({} as WindowedProjectTree)
    );
  }

  return {
    children,
    isOpen,
    props:
      id === parentId
        ? {
            id,
            type: 'root-folder',
          }
        : {
            id,
            isBoundary,
            isLoading,
            parentId,
            type: 'folder',
          },
  };
}

/**
 * A helper function for calling the getFolderAssets lazy query.
 *
 * @param getFolderAssets The return value of useGetFolderAssetsLazyQuery
 * @param folderId The id of the folder to fetch
 * @returns folderAssets The folderAssets portion of the response of GetFolderAssets
 */
async function fetchFolders(
  getFolderAssets: ReturnType<typeof useGetFolderAssetsLazyQuery>[0],
  accountId: string,
  folderId: string
) {
  const result = await getFolderAssets({
    fetchPolicy: 'cache-and-network',
    variables: {
      accountId,
      assetType: 'FOLDER',
      folderId,
      page: {
        first: PAGE_SIZE,
      },
    },
  });

  if (result.error) {
    throw new Error(result.error.message);
  }

  return getFolderAssetsConnection(result.data) ?? null;
}
